import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KeyRound, Check, Copy } from "lucide-react";
import { toast } from "sonner";

const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

/* Users & Roles → Password reset requests: people who sign in with their HR
   employee code and pressed "Forgot password?" on the login page. "Reset HR
   login" gives them a temporary HR password (with the admin's own HR sign-in);
   they must choose their own at their next sign-in. Shown only when there are
   open requests. */
export default function PasswordResetRequests() {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const [temp, setTemp] = useState(null);       // {name, username, temp_password}

  const load = useCallback(async () => {
    try { const { data } = await api.get("/password-reset-requests"); setList(data); } catch { /* admins only; ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetHr = async (r) => {
    if (!window.confirm(`Give ${r.name} (${r.hr_emp_code}) a new temporary HR password? Their current password stops working.`)) return;
    setBusy(r.id);
    try {
      const { data } = await api.post("/hr/app/_erp/reset-login", { emp_code: r.hr_emp_code });
      setTemp({ name: r.name, ...data });
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const close = async (r) => {
    setBusy(r.id);
    try { await api.post(`/password-reset-requests/${r.id}/close`); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  if (!list.length && !temp) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3" data-testid="password-reset-requests">
      <div className="flex items-center gap-2 font-semibold text-slate-900"><KeyRound className="w-4 h-4 text-amber-600" /> Password reset requests</div>
      {temp && (
        <div className="rounded-md border border-green-200 bg-white px-3 py-2 text-sm" data-testid="password-reset-temp">
          Tell <b>{temp.name}</b> to sign in with employee code <b className="font-mono">{temp.username}</b> and this temporary password:{" "}
          <b className="font-mono">{temp.temp_password}</b>
          <button type="button" className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-1"
            onClick={() => navigator.clipboard?.writeText(temp.temp_password).then(() => toast.success("Copied"))}><Copy className="w-3 h-3" />Copy</button>
          <div className="text-xs text-slate-500 mt-1">They'll be asked to choose their own password right after signing in. It isn't shown again.</div>
          <button type="button" className="text-xs text-slate-500 hover:underline mt-1" onClick={() => setTemp(null)}>Hide</button>
        </div>
      )}
      {list.map((r) => (
        <div key={r.id} className="flex items-center gap-3 flex-wrap bg-white rounded-md border border-slate-200 px-3 py-2 text-sm">
          <div className="flex-1 min-w-[200px]">
            <b>{r.name}</b> <span className="font-mono text-xs text-slate-500">{r.hr_emp_code || r.email}</span>
            <div className="text-xs text-slate-500">asked {r.requested_at ? new Date(r.requested_at).toLocaleString("en-GB") : ""}</div>
          </div>
          {r.hr_emp_code && (
            <Button size="sm" onClick={() => resetHr(r)} disabled={busy === r.id} data-testid={`reset-hr-login-${r.hr_emp_code}`}>
              <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset HR login
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => close(r)} disabled={busy === r.id}><Check className="w-3.5 h-3.5 mr-1" /> Done</Button>
        </div>
      ))}
      <p className="text-[11px] text-slate-500">Resetting needs your own HR sign-in (HR → HR App) as an HR admin with User management.</p>
    </div>
  );
}
